import { useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SupportPanel } from "@/components/support/support-panel";

/**
 * „Parašyk mums" without leaving the screen you are on.
 *
 * The same `SupportChat` the page mounts — the modal exists because the thing
 * you want to describe is usually the thing you are looking at, and navigating
 * away to describe it loses it. It attaches the current path for that reason.
 *
 * The way out to the full page is always offered: a modal is a bad place to be
 * if the conversation turns out to be long, and the page is the linkable one.
 */
export function SupportDialog({
  trigger,
  path,
  learnerId,
  open: controlledOpen,
  onOpenChange,
}: {
  /** Omit when driving it with `open` — a menu item cannot be a trigger, because
   *  the menu unmounts on click and takes the dialog with it. Same shape as
   *  `AvatarActions`'s controlled `stylesOpen`. */
  trigger?: React.ReactNode;
  path?: string;
  learnerId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const here = path ?? (typeof window === "undefined" ? undefined : window.location.pathname);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" />
            Parašyk mums
          </DialogTitle>
          <DialogDescription>
            Aprašyk, kas nutiko. Jei reikės, perduosime administratoriui.
          </DialogDescription>
        </DialogHeader>

        <SupportPanel path={here} learnerId={learnerId} />

        <div className="flex flex-col gap-2 border-t pt-3 text-xs text-muted-foreground">
          <Link
            to={`/pagalba?from=${encodeURIComponent(here ?? "")}`}
            className="inline-flex items-center gap-1 text-primary hover:underline"
            onClick={() => setOpen(false)}
          >
            Atidaryti atskirame puslapyje
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
